/**
 * usePrefetch Hook
 * 
 * Prefetch routes when user hovers over links or based on other triggers
 * This makes navigation feel instant
 */
import { useEffect, useCallback } from 'react';

// Cache to track already prefetched modules
const prefetchedModules = new Set();

/**
 * Prefetch a lazy-loaded component
 * @param {Function} importFunc - The dynamic import function (e.g., () => import('./Page'))
 */
const prefetchComponent = (importFunc) => {
  const key = importFunc.toString();
  
  if (!prefetchedModules.has(key)) {
    prefetchedModules.add(key);
    importFunc();
  }
};

/**
 * Hook to prefetch routes on hover
 * @param {Function} importFunc - The dynamic import function
 * @returns {object} - { onMouseEnter, onFocus } props to attach to element
 */
export const usePrefetch = (importFunc) => {
  const handlePrefetch = useCallback(() => {
    if (importFunc) {
      prefetchComponent(importFunc);
    }
  }, [importFunc]);

  return {
    onMouseEnter: handlePrefetch,
    onFocus: handlePrefetch,
  };
};

/**
 * Prefetch routes after initial page load (idle time)
 * @param {array} routes - Array of import functions to prefetch
 * @param {number} delay - Delay in ms before prefetching (default 2000)
 */
export const usePrefetchOnIdle = (routes = [], delay = 2000) => {
  useEffect(() => {
    // Prefetch after a delay when browser is idle
    const timer = setTimeout(() => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          routes.forEach(importFunc => prefetchComponent(importFunc));
        }, { timeout: 5000 });
      } else {
        routes.forEach(importFunc => prefetchComponent(importFunc));
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [routes, delay]);
};

/**
 * Prefetch based on user intent/behavior
 * Example: Prefetch /dashboard when user is on /login
 */
export const usePrefetchOnIntent = () => {
  const prefetchDashboard = useCallback(() => {
    import('@/pages/Dashboard');
    import('@/pages/BuildLeads');
  }, []);

  const prefetchAuth = useCallback(() => {
    import('@/pages/LoginPage');
    import('@/pages/SignupPage');
  }, []);

  return {
    prefetchDashboard,
    prefetchAuth,
  };
};

export default usePrefetch;
